import { useState, useCallback, useRef } from 'react';
import chatService from '../services/chatService';

const useChat = () => {
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // LLM konusma oturum ID'si — backend'den alinir, her istekte geri gonderilir
  const [sessionId, setSessionId] = useState(null);
  // Secim yolu takibi (dinamik LLM prompt icin)
  const [selectionPath, setSelectionPath] = useState([]);
  // Kart karsilastirma modu
  const [compareMode, setCompareMode] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]);
  // Checklist bazli paket oneri modu
  const [checklistMode, setChecklistMode] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState([]);
  // Multi-step wizard modu (RECOMMEND_CHECKLIST with grandchildren)
  const [wizardMode, setWizardMode] = useState(false);
  const [wizardSteps, setWizardSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [wizardSelections, setWizardSelections] = useState({});
  // Inline options: sadece son optionSetId aktif, eskiler devre disi
  const [latestOptionSetId, setLatestOptionSetId] = useState(0);
  const optionSetIdRef = useRef(0);

  /**
   * Options'i hem state'e hem messages dizisine ekler (inline gosterim icin)
   */
  const pushOptionsMessage = useCallback((opts) => {
    if (!opts || opts.length === 0) {
      setOptions([]);
      return;
    }
    optionSetIdRef.current += 1;
    const newId = optionSetIdRef.current;
    setOptions(opts);
    setLatestOptionSetId(newId);
    setMessages((prev) => [...prev, {
      id: Date.now() + 2,
      type: 'options',
      optionSetId: newId,
      options: opts,
      timestamp: new Date(),
    }]);
  }, []);

  /**
   * Chat'i baslatir
   */
  const startChat = useCallback(async (customerName, customerSurname, customerPhone, kvkkAccepted = true, acikRizaAccepted = true) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await chatService.startChat(customerName, customerSurname, customerPhone, kvkkAccepted, acikRizaAccepted);
      setMessages([
        {
          id: Date.now(),
          type: 'bot',
          content: response.message,
          timestamp: new Date(),
        },
      ]);
      // Options'i inline olarak messages icine gom
      pushOptionsMessage(response.options || []);
      // Backend'den gelen sessionId'yi sakla — sonraki isteklerde kullanilacak
      if (response.sessionId) {
        setSessionId(response.sessionId);
      }
    } catch (err) {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
      console.error('Start chat error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [pushOptionsMessage]);

  /**
   * Secenek secildiginde cagrilir
   */
  const selectOption = useCallback(async (option) => {
    setIsLoading(true);
    setError(null);

    // Karsilastirma modundan cikis (COMPARE_CARDS haric)
    if (option.optionType !== 'COMPARE_CARDS') {
      setCompareMode(false);
      setSelectedCards([]);
    }
    // Checklist/wizard modundan cikis (RECOMMEND_CHECKLIST haric)
    if (option.optionType !== 'RECOMMEND_CHECKLIST') {
      setChecklistMode(false);
      setSelectedCriteria([]);
      setWizardMode(false);
      setWizardSteps([]);
      setCurrentStepIndex(0);
      setWizardSelections({});
    }

    // Kullanici mesajini ekle
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: `${option.emoji || ''} ${option.displayText}`.trim(),
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // sessionId'yi de gonder — backend gecmise kaydetsin
      const response = await chatService.selectOption(option.optionType, sessionId);

      // Bot cevabini ekle
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.message,
        redirectUrl: response.redirectUrl,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
      pushOptionsMessage(response.options || []);

      // Secim yolunu guncelle
      if (option.optionType === 'MAIN_MENU') {
        setSelectionPath([]);
      } else {
        setSelectionPath((prev) => [...prev, option.displayText]);
      }

      // COMPARE_CARDS secildiyse karsilastirma modunu ac
      if (option.optionType === 'COMPARE_CARDS') {
        setCompareMode(true);
        setSelectedCards([]);
      }

      // RECOMMEND_CHECKLIST secildiyse: grandchildren varsa wizard, yoksa flat checklist
      if (option.optionType === 'RECOMMEND_CHECKLIST') {
        const opts = response.options || [];
        const hasWizardSteps = opts.some(o => o.children?.length > 0);
        if (hasWizardSteps) {
          setWizardMode(true);
          setWizardSteps(opts.filter(o => o.children?.length > 0));
          setCurrentStepIndex(0);
          setWizardSelections({});
        } else {
          setChecklistMode(true);
          setSelectedCriteria([]);
        }
      }

      // sessionId guncellemesi (normalde degismez ama yeni session acilarsa)
      if (response.sessionId) {
        setSessionId(response.sessionId);
      }
    } catch (err) {
      setError('İşlem başarısız. Lütfen tekrar deneyin.');
      console.error('Select option error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, pushOptionsMessage]);

  /**
   * Karsilastirma modunda kart secimi toggle (max 2)
   */
  const toggleCardSelection = useCallback((option) => {
    setSelectedCards((prev) => {
      const exists = prev.find((c) => c.optionType === option.optionType);
      if (exists) {
        return prev.filter((c) => c.optionType !== option.optionType);
      }
      if (prev.length >= 2) return prev;
      return [...prev, option];
    });
  }, []);

  /**
   * Checklist modunda kriter secimi toggle (limit yok)
   */
  const toggleCriteriaSelection = useCallback((option) => {
    setSelectedCriteria((prev) => {
      const exists = prev.find((c) => c.optionType === option.optionType);
      if (exists) return prev.filter((c) => c.optionType !== option.optionType);
      return [...prev, option];
    });
  }, []);

  /**
   * Kullanicinin serbest metin mesajini LLM'e gonderir.
   * Intent siniflandirma, parametre toplama ve cevap uretme backend'de yapilir.
   */
  const sendMessage = useCallback(async (text, displayText = null) => {
    if (!text || !text.trim()) return;

    setIsLoading(true);
    setError(null);
    setSelectionPath([]); // LLM'e mesaj gidince path sifirlanir

    // Kullanici mesajini aninda ekle (UX: mesaj hemen gorunsun)
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: (displayText || text).trim(), // Chat'te displayText goster
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const response = await chatService.sendMessage(text.trim(), sessionId);

      // Bot (LLM) cevabini ekle
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.message,
        isAiResponse: true, // LLM tarafindan uretildi
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);

      // LLM cevabinda options varsa inline olarak goster
      if (response.options && response.options.length > 0) {
        pushOptionsMessage(response.options);
      }

      // sessionId guncellemesi
      if (response.sessionId) {
        setSessionId(response.sessionId);
      }
    } catch (err) {
      // 503 = LLM servisi kullanilamiyor — fallback mesaj
      if (err.response && err.response.status === 503) {
        setError('Yapay zeka servisi şu anda kullanılamıyor. Lütfen seçeneklerden birini kullanın.');
      } else {
        setError('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
      }
      console.error('Send message error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, pushOptionsMessage]);

  /**
   * Wizard adimi bazinda secim toggle
   */
  const toggleWizardSelection = useCallback((stepOptionType, item) => {
    setWizardSelections((prev) => {
      const current = prev[stepOptionType] || [];
      const exists = current.find((c) => c.optionType === item.optionType);
      if (exists) {
        return { ...prev, [stepOptionType]: current.filter((c) => c.optionType !== item.optionType) };
      }
      return { ...prev, [stepOptionType]: [...current, item] };
    });
  }, []);

  /**
   * Wizard bir sonraki adima gec
   */
  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.min(prev + 1, wizardSteps.length - 1));
  }, [wizardSteps.length]);

  /**
   * Wizard bir onceki adima don
   */
  const goToPrevStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  /**
   * Wizard tum adimlardaki secimleri LLM'e gonderir
   */
  const submitWizard = useCallback(() => {
    const allSelections = wizardSteps.map((step) => {
      const selected = wizardSelections[step.optionType] || [];
      if (selected.length === 0) return null;
      return `${step.displayText}: ${selected.map((s) => s.displayText).join(', ')}`;
    }).filter(Boolean);
    if (allSelections.length === 0) return;
    const criteriaText = allSelections.join('; ');
    const displayText = `🎯 Kriterlerime göre paket öner: ${criteriaText}`;
    setWizardMode(false);
    setWizardSteps([]);
    setCurrentStepIndex(0);
    setWizardSelections({});
    sendMessage(
      `Bana en uygun ucus paketini oner. Benim icin onemli kriterler: ${criteriaText}. ` +
      `Lutfen mevcut paketleri bu kriterlere gore degerlendir, uygun olmayanlari ele ve en iyi esleseni oner. ` +
      `Neden bu paketi onerdigini acikla.`,
      displayText
    );
  }, [wizardSteps, wizardSelections, sendMessage]);

  /**
   * Secilen 2 karti karsilastirma mesaji olarak LLM'e gonderir
   */
  const submitComparison = useCallback(() => {
    if (selectedCards.length !== 2) return;
    const card1 = selectedCards[0].displayText;
    const card2 = selectedCards[1].displayText;
    setCompareMode(false);
    setSelectedCards([]);
    sendMessage(`${card1} ile ${card2} kartını karşılaştır`);
  }, [selectedCards, sendMessage]);

  /**
   * Secilen kriterleri oneri mesaji olarak LLM'e gonderir
   */
  const submitRecommendation = useCallback(() => {
    if (selectedCriteria.length === 0) return;
    const criteriaList = selectedCriteria.map((c) => c.displayText).join(', ');
    const displayText = `🎯 Kriterlerime göre paket öner: ${criteriaList}`;
    setChecklistMode(false);
    setSelectedCriteria([]);
    sendMessage(
      `Bana en uygun ucus paketini oner. Benim icin onemli kriterler: ${criteriaList}. ` +
      `Lutfen mevcut paketleri bu kriterlere gore degerlendir, uygun olmayanlari ele ve en iyi esleseni oner. ` +
      `Neden bu paketi onerdigini acikla.`,
      displayText
    );
  }, [selectedCriteria, sendMessage]);

  /**
   * Root secenekleri yeniden yukler (bot'a geri donuste)
   */
  const refreshOptions = useCallback(async () => {
    try {
      const opts = await chatService.getOptions();
      pushOptionsMessage(opts || []);
    } catch (err) {
      console.error('Refresh options error:', err);
    }
  }, [pushOptionsMessage]);

  /**
   * Chat'i sifirlar
   */
  const resetChat = useCallback(() => {
    setMessages([]);
    setOptions([]);
    setError(null);
    setSessionId(null);
    setSelectionPath([]);
    setCompareMode(false);
    setSelectedCards([]);
    setChecklistMode(false);
    setSelectedCriteria([]);
    setWizardMode(false);
    setWizardSteps([]);
    setCurrentStepIndex(0);
    setWizardSelections({});
    setLatestOptionSetId(0);
    optionSetIdRef.current = 0;
  }, []);

  return {
    messages,
    options,
    isLoading,
    error,
    sessionId,
    selectionPath,
    compareMode,
    selectedCards,
    startChat,
    selectOption,
    sendMessage,
    toggleCardSelection,
    submitComparison,
    checklistMode,
    selectedCriteria,
    toggleCriteriaSelection,
    submitRecommendation,
    wizardMode,
    wizardSteps,
    currentStepIndex,
    wizardSelections,
    toggleWizardSelection,
    goToNextStep,
    goToPrevStep,
    submitWizard,
    refreshOptions,
    resetChat,
    latestOptionSetId,
  };
};

export default useChat;
